import { useId } from 'react';
import { X, ArrowRight } from 'lucide-react';
import { Dialog } from '../ui';
import { safeId } from '../ui/labelledControl';
import {
  getTeamPlayerIds,
  getEloLevel,
  getDisplayTournamentId,
  getDisplayMatchId,
} from '../../engine';

// ─────────────────────────────────────────────────────────────────────
// ArchiveMatchDetailModal — match details for the "All matches" tab.
// Extracted verbatim from the original monolith (legacy/App.jsx). Shows a
// compact summary of the match with a link to the source activity. Handles
// both slot format (Padel24) and flat format (Americano, single match).
//
// Engine swaps (original monolith → src/engine named imports, same functions):
//   getTeamPlayerIds / getEloLevel / getDisplayTournamentId / getDisplayMatchId
//     → '../../engine'
// ─────────────────────────────────────────────────────────────────────

export interface ArchiveMatchDetailModalProps {
  entry: any;
  onClose: () => void;
  onOpenSource: () => void;
  onPlayerClick?: (p: any) => void;
}

export function ArchiveMatchDetailModal({
  entry,
  onClose,
  onOpenSource,
  onPlayerClick,
}: ArchiveMatchDetailModalProps) {
  const {
    match: m,
    typeInfo,
    sourceName,
    sourceType,
    sourceData,
    playerById,
    playedAt,
    sourceIsVirtual,
  } = entry;
  const titleId = safeId('archive-match-title', useId());

  const [t1p1Id, t1p2Id] = getTeamPlayerIds(m.team1);
  const [t2p1Id, t2p2Id] = getTeamPlayerIds(m.team2);
  const t1p1 = playerById[t1p1Id];
  const t1p2 = playerById[t1p2Id];
  const t2p1 = playerById[t2p1Id];
  const t2p2 = playerById[t2p2Id];

  const playerEloPre = m.playerEloPre || {};
  const getElo = (id, fallback) => {
    if (playerEloPre[id] != null) return playerEloPre[id];
    return fallback;
  };

  const t1Won = m.score1 > m.score2;
  const t2Won = m.score2 > m.score1;

  const dateStr = playedAt
    ? new Date(playedAt).toLocaleDateString('no-NO', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : 'Ukjent dato';

  // Global IDs — uses simpler display IDs (T1, M1) for the interface.
  // (The original monolith also computed an unused `globalMatchId` here — dead
  // in the original and dropped on extraction to keep lint clean; the displayed
  // tournament/match ids below are unchanged.)
  const tournamentDisplayId = getDisplayTournamentId(sourceData);
  const matchDisplayId = getDisplayMatchId(sourceData, m);

  // Set details if available
  const sets = m.sets && Array.isArray(m.sets) ? m.sets : null;

  // Context label (phase/round for P24, round for AM)
  let contextLabel = '';
  if (sourceType === 'padel24') {
    if (m.phase === 'phase1') contextLabel = `Fase 1${m.round ? ` · Runde ${m.round}` : ''}`;
    else if (m.phase === 'phase2') contextLabel = `Fase 2${m.round ? ` · Runde ${m.round}` : ''}`;
    else if (m.phase === 'finals') contextLabel = 'Finale';
    if (m.court) contextLabel += contextLabel ? ` · Bane ${m.court}` : `Bane ${m.court}`;
  } else if (sourceType === 'americano') {
    if (m.isFinal) contextLabel = `Finale${m.finalNumber ? ` ${m.finalNumber}` : ''}`;
    else contextLabel = `Runde ${m.round || '?'}`;
    if (m.court) contextLabel += ` · Bane ${m.court}`;
  } else if (sourceType === 'single_matches') {
    contextLabel = 'Enkeltkamp';
  }

  const renderPlayerLine = (p, isWinner) => {
    if (!p) return <span className="text-ink-3 italic text-sm">—</span>;
    const elo = getElo(p.id, p.elo);
    const level = getEloLevel(elo);
    return (
      <span className="inline-flex items-center gap-1.5">
        <span
          className="font-mono text-[9px] font-bold px-1 rounded"
          style={{ background: level.color, color: level.text }}
          title={`Nivå ${level.level} — ${level.name}`}
        >
          {level.level}
        </span>
        <button
          onClick={() => onPlayerClick?.(p)}
          className={`hover:underline text-sm ${isWinner ? 'font-bold text-ink' : 'text-ink-2'}`}
        >
          {p.name}
        </button>
        <span className="font-mono text-[10px] text-ink-3">{elo}</span>
      </span>
    );
  };

  return (
    // A `Dialog` (#900), named by the activity the match came from. Escape and
    // the backdrop close it, and focus goes back to the match row.
    <Dialog
      labelledBy={titleId}
      onClose={onClose}
      showClose={false}
      backdropClassName=""
      backdropStyle={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        background: 'rgba(15, 22, 17, 0.55)',
      }}
      className=""
      style={{
        background: '#ffffff',
        maxWidth: 560,
        width: '100%',
        borderRadius: 16,
        boxShadow: '0 25px 60px -10px rgba(0,0,0,0.4)',
        border: '1px solid #e7eae3',
        maxHeight: '90vh',
        overflow: 'auto',
      }}
    >
      {/* Header */}
      <div className="px-5 pt-5 pb-4 border-b border-line">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              <span
                className="font-mono text-[10px] font-bold px-2 py-0.5 rounded"
                style={{ background: typeInfo.color + '40', color: '#0f1611' }}
              >
                {typeInfo.label}
              </span>
              {contextLabel && (
                <span className="font-mono text-[10px] text-ink-3">{contextLabel}</span>
              )}
            </div>
            <h3 id={titleId} className="font-display text-lg font-semibold leading-tight mb-0.5">
              {sourceName}
            </h3>
            <div className="text-xs text-ink-3 capitalize mb-1.5">{dateStr}</div>
            {/* Tracking IDs (archive only) */}
            <div className="font-mono text-[10px] text-ink-3 space-y-0.5 mt-1.5 pt-1.5 border-t border-line-soft">
              <div>
                Turnerings-ID:{' '}
                <span className="text-ink-2 select-all font-semibold">{tournamentDisplayId}</span>
              </div>
              <div>
                Match-ID:{' '}
                <span className="text-ink-2 select-all font-semibold">{matchDisplayId}</span>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex-shrink-0 p-1.5 rounded-full text-ink-3 hover:bg-bg-soft transition-colors"
            aria-label="Lukk"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Score block */}
      <div className="px-5 py-4">
        {/* Team 1 (home team — blue) */}
        <div
          className="rounded-lg p-3 mb-1.5"
          style={{
            background: 'rgba(96, 165, 250, 0.10)',
            border: t1Won ? '1px solid rgba(96, 165, 250, 0.45)' : '1px solid transparent',
          }}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex flex-col gap-1 min-w-0">
              {renderPlayerLine(t1p1, t1Won)}
              {renderPlayerLine(t1p2, t1Won)}
            </div>
            <div className="flex items-baseline gap-3 flex-shrink-0">
              {m.teamElo1Pre != null && (
                <span className="font-mono text-xs text-ink-3">
                  {m.teamElo1Pre}
                  {m.eloChange1 != null && (
                    <span
                      className="ml-1 font-bold"
                      style={{ color: m.eloChange1 >= 0 ? '#84cc16' : '#f43f5e' }}
                    >
                      {m.eloChange1 >= 0 ? '+' : ''}
                      {m.eloChange1}
                    </span>
                  )}
                </span>
              )}
              <span
                className="font-display text-3xl font-bold"
                style={{ color: t1Won ? '#84cc16' : '#94a3a4' }}
              >
                {m.score1 ?? '–'}
              </span>
            </div>
          </div>
        </div>

        <div className="text-center text-[10px] font-mono uppercase tracking-widest text-ink-4 my-1">
          vs
        </div>

        {/* Team 2 (away team — orange) */}
        <div
          className="rounded-lg p-3"
          style={{
            background: 'rgba(251, 146, 60, 0.10)',
            border: t2Won ? '1px solid rgba(251, 146, 60, 0.45)' : '1px solid transparent',
          }}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex flex-col gap-1 min-w-0">
              {renderPlayerLine(t2p1, t2Won)}
              {renderPlayerLine(t2p2, t2Won)}
            </div>
            <div className="flex items-baseline gap-3 flex-shrink-0">
              {m.teamElo2Pre != null && (
                <span className="font-mono text-xs text-ink-3">
                  {m.teamElo2Pre}
                  {m.eloChange2 != null && (
                    <span
                      className="ml-1 font-bold"
                      style={{ color: m.eloChange2 >= 0 ? '#84cc16' : '#f43f5e' }}
                    >
                      {m.eloChange2 >= 0 ? '+' : ''}
                      {m.eloChange2}
                    </span>
                  )}
                </span>
              )}
              <span
                className="font-display text-3xl font-bold"
                style={{ color: t2Won ? '#84cc16' : '#94a3a4' }}
              >
                {m.score2 ?? '–'}
              </span>
            </div>
          </div>
        </div>

        {/* Set details (if available) */}
        {sets && sets.length > 0 && (
          <div className="mt-4 pt-4 border-t border-line">
            <div className="text-[10px] font-mono uppercase tracking-widest text-ink-3 mb-2">
              Sett
            </div>
            <div className="flex gap-2 flex-wrap">
              {sets.map((s, i) => {
                if (!s) return null;
                const g1 = s.games1 ?? 0;
                const g2 = s.games2 ?? 0;
                if (g1 === 0 && g2 === 0) return null;
                const setT1Won = g1 > g2;
                return (
                  <div key={i} className="card p-2 text-center min-w-[64px]">
                    <div className="text-[9px] font-mono text-ink-3 uppercase mb-0.5">
                      Sett {i + 1}
                    </div>
                    <div className="font-mono text-sm font-semibold">
                      <span style={{ color: setT1Won ? '#84cc16' : '#94a3a4' }}>{g1}</span>
                      <span className="text-ink-4 mx-1">–</span>
                      <span style={{ color: !setT1Won ? '#84cc16' : '#94a3a4' }}>{g2}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Footer actions */}
      <div className="px-5 pb-5 flex gap-2 justify-end border-t border-line pt-4">
        <button type="button" onClick={onClose} className="btn-ghost text-sm">
          Lukk
        </button>
        <button
          type="button"
          onClick={onOpenSource}
          className="btn-outline text-sm"
          title={sourceIsVirtual ? 'Åpne enkeltkamper-listen' : 'Åpne hele aktiviteten'}
        >
          <ArrowRight size={12} /> Åpne {sourceIsVirtual ? 'enkeltkamper' : 'aktivitet'}
        </button>
      </div>
    </Dialog>
  );
}
